// ---------------------------------------------------------------------------
// Design tokens — Wave 4.1.A
//
// Single source of truth for colour, spacing, typography, shadows, radii, and
// breakpoints across the app. Pages import these instead of inlining hex
// codes / pixel values.
//
// CRITICAL: merchant-branding CSS variables.
//
// BrandingContext writes --brand-primary, --brand-secondary, --brand-accent
// to :root at runtime per merchant (white-label). The `color.brand.*` tokens
// reference those vars with hex fallbacks so primitives stay branded for the
// merchant's customers, and gracefully fall back to ReCurr defaults for
// platform-level surfaces (admin, internal pages, the signed-out marketing
// shell).
//
// Token NAMING RULE: semantic over scale.
//   color.text.primary   ✅
//   color.gray[950]      ❌ (renaming gray.50 → gray.100 is a 200-file churn)
// ---------------------------------------------------------------------------

/**
 * THE RECURR PALETTE — read off the logo, and the only place it is defined.
 *
 * Sampled from `Images/recur-logo-dark-navy-orange-complete-pwrdby.png`: the
 * arrow ring and "Re" are navy, the R and "Curr" are orange. Before this the
 * brand colour had FOUR definitions in the frontend — a blue, an older
 * blue, an indigo and bootstrap's blue — none of which was in the logo.
 *
 * Which colour does which job, and why — the contrast ratios decide it:
 *
 *   navy   → every primary action. White on navy is 13.74:1, so it is a
 *            drop-in for the blue it replaces and passes AA everywhere.
 *   orange → the ACQUISITION call-to-action only (get started / choose a
 *            plan), and it carries a NAVY label, not a white one. White on
 *            this orange is 2.95:1 — it fails AA for text (4.5:1) and even
 *            for controls (3:1), so a white-on-orange button cannot ship.
 *            Navy on orange is 4.66:1 and is the logo's own pairing.
 *            The navy border is not decoration: orange against a white page
 *            is 2.95:1, below the 3:1 a control's boundary needs.
 *
 * `orangeInk` exists because the logo orange is 2.95:1 on white and cannot be
 * read as small text — a ✓ tick, a badge label, an inline emphasis. Use the
 * logo orange for FILLS and `orangeInk` for GLYPHS; they are not interchangeable
 * and swapping them fails AA in one direction and looks muddy in the other.
 *
 * A note for anyone restyling an orange control: the pressed/active tone has to
 * LIGHTEN, not darken. Navy on the base orange is already 4.66:1, so anything
 * darker drops the label under 4.5:1 — which is why `orangeHover` is the deeper
 * state's colour too. Checked against the ratios, not assumed from the pattern
 * navy uses (navy darkens on press, because its label is white).
 *
 * Deliberately NOT here: the logo's #6f6c6c "Powered by" grey. The grey
 * buttons on the banners are grey by founder ruling and keep their own
 * neutral values — do not re-point them at a brand token.
 */
export const brandPalette = {
  navy:        '#1e2c4f',  // logo navy — primary action, white label (13.74:1)
  navyHover:   '#2a3d6b',  // lifts on hover (white 10.62:1)
  navyDeep:    '#16213b',  // pressed / active (white 15.97:1)
  navyMuted:   '#97a3bd',  // disabled primary
  orange:      '#e67926',  // logo orange — acquisition CTA, navy label (4.66:1)
  orangeHover: '#ea8437',  // lightens on hover (navy 5.14:1)
  inkOnOrange: '#1e2c4f',  // the label colour on orange. Never #fff.
  orangeInk:   '#c2410c',  // TEXT-weight orange — ticks, badges (4.8:1 on white)
} as const;

export const color = {
  /**
   * Brand colours — wired to merchant CSS variables with platform fallback.
   * Pages should default to these for any "primary action" UI surface.
   *
   * The fallbacks are ReCurr's own colours; the variables are the MERCHANT's.
   * A merchant who has set white-label branding still overrides all three —
   * changing these hexes re-colours ReCurr's surfaces, never theirs.
   */
  brand: {
    primary:   `var(--brand-primary, ${brandPalette.navy})`,
    secondary: `var(--brand-secondary, ${brandPalette.navyDeep})`,
    accent:    `var(--brand-accent, ${brandPalette.orange})`,
  },

  /** Text colours. */
  text: {
    primary:   '#111827',  // headings, body
    secondary: '#374151',  // labels, secondary copy
    muted:     '#6b7280',  // captions, helper text, timestamps
    subtle:    '#9ca3af',  // disabled, placeholder
    inverse:   '#ffffff',  // text on dark backgrounds
    link:      `var(--brand-primary, ${brandPalette.navy})`,
    error:     '#991b1b',
    success:   '#166534',
    warning:   '#92400e',
  },

  /** Background colours for cards, panels, page chrome. */
  bg: {
    page:    '#f9fafb',
    card:    '#ffffff',
    subtle:  '#f3f4f6',
    muted:   '#f9fafb',
    hover:   '#f3f4f6',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },

  /** Border colours. */
  border: {
    default: '#e5e7eb',
    strong:  '#d1d5db',
    subtle:  '#f3f4f6',
    focus:   `var(--brand-primary, ${brandPalette.navy})`,
  },

  /** Status colours for badges, alerts, banners. */
  status: {
    success: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
    warning: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
    error:   { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
    info:    { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
    neutral: { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' },
  },
} as const;

/**
 * Chart colours (B123) — the brand navy's OWN hue (OKLCH h ≈ 266°) stepped in
 * lightness, so a chart reads as ReCurr's without bringing in a new hue. Run
 * through the dataviz skill's validator, light surface #ffffff:
 *   · `series` — one series per chart: L 0.54, C 0.105, 5.12:1 on white
 *     (inside the lightness band, over the chroma floor, over 3:1 for a mark).
 *   · `ordinal` — five ordered steps for ordered buckets (invoice age), light →
 *     dark, the darkest IS brandPalette.navy: lightness monotone, every adjacent
 *     ΔL ≥ 0.06, one hue (1° spread), light end 2.23:1 (≥ 2:1). ⚠ That light end
 *     is under 3:1, so a chart using it must label its marks and keep the table.
 * Text never wears these — axis and value labels use the text tokens.
 */
export const chartPalette = {
  series:   '#526cac',
  ordinal:  ['#9caed5', '#758dc2', '#526cac', '#364d86', brandPalette.navy] as readonly string[],
  grid:     '#e5e7eb',  // = color.border.default
  baseline: '#d1d5db',  // = color.border.strong
  axisText: '#6b7280',  // = color.text.muted (4.83:1)
} as const;

/** Spacing scale in pixels. Add the unit when consuming: `${space[4]}px`. */
export const space = {
  0:   0,
  1:   4,
  2:   8,
  3:   12,
  4:   16,
  5:   20,
  6:   24,
  8:   32,
  10:  40,
  12:  48,
  16:  64,
  20:  80,
  24:  96,
} as const;

/** Typography scale. */
export const font = {
  family: {
    sans: '"Inter", "Helvetica Neue", system-ui, sans-serif',
    mono: '"JetBrains Mono", "SF Mono", Consolas, monospace',
  },
  size: {
    xs:   12,
    sm:   13,
    base: 14,
    md:   15,
    lg:   16,
    xl:   18,
    '2xl': 20,
    '3xl': 24,
    '4xl': 30,
    '5xl': 36,
  },
  weight: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },
  lineHeight: {
    tight:   1.2,
    snug:    1.35,
    normal:  1.5,
    relaxed: 1.65,
  },
} as const;

/** Shadows. */
export const shadow = {
  none: 'none',
  xs:   '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm:   '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
  md:   '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05)',
  lg:   '0 10px 15px rgba(0, 0, 0, 0.10), 0 4px 6px rgba(0, 0, 0, 0.04)',
  xl:   '0 20px 25px rgba(0, 0, 0, 0.12), 0 10px 10px rgba(0, 0, 0, 0.04)',
  focusRing: '0 0 0 3px var(--brand-primary-25, rgba(37, 99, 235, 0.25))',
} as const;

/** Border radius. */
export const radius = {
  none: 0,
  sm:   4,
  md:   6,
  lg:   8,
  xl:   12,
  full: 9999,
} as const;

/** Breakpoints in pixels — mobile-first. */
export const breakpoint = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

/** Media-query helpers. */
export const mq = {
  sm:  `@media (min-width: ${breakpoint.sm}px)`,
  md:  `@media (min-width: ${breakpoint.md}px)`,
  lg:  `@media (min-width: ${breakpoint.lg}px)`,
  xl:  `@media (min-width: ${breakpoint.xl}px)`,
  // Mobile-only (max-width)
  mobile: `@media (max-width: ${breakpoint.md - 1}px)`,
} as const;

/** Z-index scale.
 *
 * IMPORTANT: the layered values here MUST sit above the legacy
 * `.dashboard-header` (z-index: 100 + position: sticky, declared in
 * index.css). The header creates its own stacking context, so any
 * fixed-position overlay with z-index < 100 will be visually clipped at
 * the bottom edge of the header — manifesting as a "modal can't be
 * interacted with at the top" bug.
 *
 * The pre-Wave-4.1.F.4 scale put modal at 70 (below the header) which
 * meant every modal in the app was partially hidden behind the header
 * — uncovered when the Cmd+K palette shipped because the search input
 * lives at the very top of the palette.
 */
export const zIndex = {
  base:        0,
  raised:      10,
  sticky:      20,
  drawer:      40,
  dropdown:    50,
  overlay:    150,  // base for full-screen overlays (above header @100)
  modal:      200,  // dialogs, sheets, command palette
  popover:    240,
  toast:      260,  // above modals so confirmations stay visible
  tooltip:    300,  // above toasts (tooltip in modal still wins)
  coachmark:  320,  // above everything else
} as const;

/** Duration tokens for transitions. */
export const duration = {
  instant: 0,
  fast:    120,
  normal:  200,
  slow:    320,
} as const;

/** Easing tokens. */
export const easing = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  enter:    'cubic-bezier(0, 0, 0.2, 1)',
  exit:     'cubic-bezier(0.4, 0, 1, 1)',
} as const;

// Derived helper types — gives compile-time typo protection on consumers.
export type ColorToken    = keyof typeof color.text;
export type StatusVariant = keyof typeof color.status;
export type SpaceToken    = keyof typeof space;
export type FontSizeToken = keyof typeof font.size;
